/* @flow */

import * as React from 'react';
import PropTypes from 'prop-types';
import { compose, getContext, withHandlers, withState } from 'recompose';
import glamorous from 'glamorous';
import { TweenMax, Power4 } from 'gsap';
import { memoize } from 'lodash/fp';
import AnimationList from 'app/components/animation/AnimationList';
import NotificationMessage from './NotificationMessage';

type Item = {
  confirmText?: string,
  id: string,
  message: any,
  onClose?: () => void,
  onConfirm?: () => void,
  type: 'danger' | 'info' | 'success' | 'warning',
};

type Props = {
  items: Array<Item>,
  subscribe: () => void,
  unsubscribe: () => void,
};

const NotificationsList = glamorous.ul({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  position: 'relative',
});

const ListItem = glamorous.li({
  paddingBottom: 10,
  position: 'absolute',
  width: '100%',
});

class NotificationsBoard extends React.Component<Props> {
  props: Props;
  nodes: Map<string, HTMLElement> = new Map(); // eslint-disable-line react/sort-comp

  componentDidMount() {
    this.props.subscribe();
  }

  componentWillUnmount() {
    this.props.unsubscribe();
    this.nodes.forEach((node) => {
      TweenMax.killTweensOf(node);
    });
  }

  handleRemove = (key: string) => TweenMax.to(this.nodes.get(key), 1, {
    x: '+=100',
    alpha: 0,
    ease: Power4.easeOut,
  });

  handleUpdate = (keys: Array<string>, prevKeys: Array<string>) => {
    let yPos = 0;

    keys.forEach((key) => {
      const node = this.nodes.get(key);

      if (prevKeys.includes(key)) {
        TweenMax.to(node, 1, { y: yPos, ease: Power4.easeOut });
      } else {
        TweenMax.set(node, { y: yPos });
        TweenMax.from(node, 1, { x: '+=100', alpha: 0, ease: Power4.easeOut });
      }
      yPos += node.offsetHeight;
    });
  };

  setRef = memoize((key: string) => (ref: HTMLElement) => {
    this.nodes.set(key, ref);
  });

  render() {
    const { items } = this.props;

    return (
      <NotificationsList>
        <AnimationList
          enterAt="top"
          onRemove={this.handleRemove}
          onUpdate={this.handleUpdate}
        >
          {items.map(item => (
            <ListItem key={item.id} innerRef={this.setRef(item.id)}>
              <NotificationMessage
                confirmText={item.confirmText}
                onClose={item.onClose}
                onConfirm={item.onConfirm}
                type={item.type}
              >
                {item.message}
              </NotificationMessage>
            </ListItem>
          ))}
        </AnimationList>
      </NotificationsList>
    );
  }
}

const withNotifications = compose(
  getContext({
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
      remove: PropTypes.func.isRequired,
      subscribe: PropTypes.func.isRequired,
      unsubscribe: PropTypes.func.isRequired,
    }).isRequired,
  }),
  withState('items', 'setItems', []),
  withHandlers({
    subscribe: ({ notifications, setItems }) => () => {
      notifications.subscribe(setItems);
    },
    unsubscribe: ({ notifications, setItems }) => () => {
      notifications.unsubscribe(setItems);
    },
  }),
);

export default withNotifications(NotificationsBoard);
